type BrowserAudioScope = {
    readonly AudioContext?: typeof AudioContext;
    // Safari exposes only the prefixed constructor.
    readonly webkitAudioContext?: typeof AudioContext;
};

export function getBrowserAudioContextConstructor():
    | typeof AudioContext
    | null {
    const scope = globalThis as unknown as BrowserAudioScope;
    return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

// One context for the whole page: browsers cap how many can exist at once and
// only reclaim one on close(), so building one per sound eventually exhausts
// the cap and then throws for the rest of the page's life.
let sharedAudioContext: AudioContext | null = null;
let hasInstalledGestureUnlock = false;
let hasDocumentHadUserActivation = false;
// These keydown inputs are excluded by the HTML activation-triggering rules.
const NON_ACTIVATING_KEYDOWN_KEYS = new Set([
    'Alt',
    'Control',
    'Escape',
    'Meta',
    'Shift',
]);

/**
 * The page-wide `AudioContext` for short UI sounds, created on first use.
 *
 * Returns `null` where the API is absent or construction fails, so callers can
 * fall back. The returned context may be `suspended` — check `state` if you
 * need to know whether sound will actually be heard.
 */
export function getSharedAudioContext(): AudioContext | null {
    const AudioContextConstructor = getBrowserAudioContextConstructor();
    if (!AudioContextConstructor) {
        return null;
    }

    // A closed context can never play again. Nothing here closes one, so this
    // only matters if something else on the page ever does.
    if (sharedAudioContext === null || sharedAudioContext.state === 'closed') {
        try {
            sharedAudioContext = new AudioContextConstructor();
        } catch {
            // Construction is what fails once the concurrent-context cap is
            // reached, so degrade instead of throwing at the caller.
            sharedAudioContext = null;
            return null;
        }
    }

    // Never helps the caller that triggered it — the promise cannot settle
    // before the caller re-reads `state` — so this is only what recovers a
    // context for a *later* sound. Autoplay policy is cleared by
    // `installGestureUnlock` instead. The listeners stay armed because iOS can
    // suspend a context every time the page is backgrounded or the screen
    // locks, and only a later gesture can reliably resume it on WebKit.
    if (sharedAudioContext.state === 'suspended') {
        void sharedAudioContext.resume().catch(() => {});
    }

    return sharedAudioContext;
}

/**
 * What to tell someone whose recording refuses to play.
 *
 * A recording URL is signed when the page loads it but only fetched when the
 * play button is pressed, so a page left open past the signature's lifetime
 * holds a URL S3 will reject. The browser reports that rejection as an
 * undecodable source, which without this reads as a play button that simply
 * does nothing. Reloading re-signs the URL, so it is the recovery to offer.
 */
export const RECORDING_PLAYBACK_ERROR_MESSAGE =
    'Recording could not be played \u2014 refresh the page to try again.';

/**
 * Start playback on a media element, ignoring interrupted-start rejections.
 *
 * `play()` resolves only once playback actually begins, so any `pause()`,
 * `load()`, or `src` change in the interim rejects it with `AbortError`. That
 * is the normal outcome of a play/stop toggle or a second seek click, not a
 * failure — but an unhandled rejection still reaches Sentry as a page error.
 * Every other rejection (autoplay refused, undecodable media) is a real
 * playback failure and is re-thrown. Callers must handle it: an ignored
 * rejection is both a Sentry page error and a control that silently does
 * nothing. `RECORDING_PLAYBACK_ERROR_MESSAGE` is what recording players show.
 */
export function playMediaElement(audio: HTMLMediaElement): Promise<void> {
    return audio.play().catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
            return;
        }
        throw error;
    });
}

/**
 * Resume the shared context on the user's first interaction.
 *
 * Autoplay policy starts a context `suspended`, and Safari honours a resume
 * only from inside a user-gesture handler. Everything that plays a sound here
 * runs from a websocket message instead, so without this the context can stay
 * suspended for the whole page life and nothing is ever heard. Idempotent, and
 * the listeners remain armed so a later gesture can resume a context that was
 * suspended by backgrounding or screen lock.
 */
export function installGestureUnlock(): void {
    if (hasInstalledGestureUnlock || typeof window === 'undefined') {
        return;
    }
    hasInstalledGestureUnlock = true;

    // `getSharedAudioContext` already resumes a suspended context; running it
    // from inside the gesture handler is the part that makes WebKit honour the
    // resume. Guarded by the test that asserts a gesture resumes the context.
    const unlock = (event: Event) => {
        const isNonActivatingKeydown =
            event.type === 'keydown' &&
            NON_ACTIVATING_KEYDOWN_KEYS.has((event as KeyboardEvent).key);
        if (!isNonActivatingKeydown) {
            hasDocumentHadUserActivation = true;
        }
        getSharedAudioContext();
    };

    for (const eventName of ['pointerdown', 'keydown', 'touchstart']) {
        window.addEventListener(eventName, unlock, {
            passive: true,
        });
    }
}

/**
 * Whether the document has ever received user activation.
 *
 * Chromium provides authoritative `navigator.userActivation` state, but
 * WebKit does not, so the listener latch is the portable fallback. The
 * browser property also covers activation before the listeners were installed.
 */
export function hasHadUserActivation(): boolean {
    if (
        typeof navigator !== 'undefined' &&
        navigator.userActivation !== undefined
    ) {
        return navigator.userActivation.hasBeenActive;
    }
    return hasDocumentHadUserActivation;
}

/** Test-only: drop the cached context and re-arm the gesture listeners. */
export function resetSharedAudioContextForTests(): void {
    sharedAudioContext = null;
    hasInstalledGestureUnlock = false;
    hasDocumentHadUserActivation = false;
}
